import {entry as castEntry, noop, objectFromEntries} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {BulkArchiveFilesOptions} from 'archive/ArchiveActions';
import {NOT_ARCHIVED_PURPOSE_STRING} from 'archive/ArchiveConstants';
import ArchivePurposeDropdown from 'archive/components/ArchivePurposeDropdown';
import * as ArchiveModal from 'archive/components/modals/ArchiveModal';
import isArchived from 'archive/isArchived';
import * as BenchlingUtils from 'BenchlingUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import EditableItemTable, {RowProps} from 'ui-library/EditableItemTable';
import PlainSearchInput, {BorderRadius} from 'ui-library/PlainSearchInput';
import strUtil from 'util/strUtil';
import {Optional} from 'util/Types';

import {TestDefinitionForListing} from './TestDefinitionsPage';

const TEST_DEFINITION_COLUMN_HEADERS = [{name: 'Test definitions', width: 12}];
const EMPTY_SEARCH_QUERY = '';

export interface RowPermissions {
  isArchivable: boolean;
  isDeletable: boolean;
}

export interface TestDefinitionsListingProps {
  columnHeaders: Array<{name: string; width: number}>;
  customSearchControls?: JSX.Element;
  customFilterCallback?: (
    test_definitions: Array<TestDefinitionForListing>
  ) => Array<TestDefinitionForListing>;
  errorMessage?: string;
  testDefinitions?: Array<TestDefinitionForListing> | null;
  testDefinitionType: string;
  navigateToCreate: () => void;
  urlForTestDefinition: (testDefinitionId: string) => string;
  archiveTestDefinitions: (itemIds: Array<string>, purpose: string, options: BulkArchiveFilesOptions) => void;
  unarchiveTestDefinitions: (itemIds: Array<string>) => void;
  deleteTestDefinition?: (itemId: string) => void;
  isWritable: boolean;
  mapTestDefinitionToRowValue: (testDefinition: TestDefinitionForListing) => Array<JSX.Element | string>;
  mapTestDefinitionToRowPermissions?: (testDefinition: TestDefinitionForListing) => RowPermissions;
}

interface State {
  selectedArchivePurposeFilters: Array<string>;
  searchQuery: string;
}

/**
 * A component that displays a list of test definitions in a table.
 *
 * Note that we are keeping this as a class component because this is heavily copied from SchemasListing.tsx
 * In order to make future transitions to a library concept easier, we are keeping this consistent with
 * the existing schemas code for now.
 */
class TestDefinitionsListing extends React.Component<TestDefinitionsListingProps, State> {
  static defaultProps = {
    columnHeaders: TEST_DEFINITION_COLUMN_HEADERS,
    isWritable: false,
    mapTestDefinitionToRowValue: (testDefinition: TestDefinitionForListing) => [
      <span>{testDefinition.name}</span>,
    ],
  };

  constructor(props: TestDefinitionsListingProps) {
    super(props);
    const initialQuery = BenchlingUtils.getQueryParam('q');
    this.state = {
      selectedArchivePurposeFilters: [NOT_ARCHIVED_PURPOSE_STRING],
      searchQuery: initialQuery || EMPTY_SEARCH_QUERY,
    };
  }

  private getTestDefinitionUrlForRow = (itemIndex: number): string | null => {
    if (this.props.testDefinitions != null) {
      const testDefinition = this.props.testDefinitions[itemIndex];
      return this.props.urlForTestDefinition(testDefinition.id);
    } else {
      return null;
    }
  };

  private getLoadedTestDefinitions(): Array<TestDefinitionForListing> {
    if (!this.props.testDefinitions) {
      throw new Error('Expected testDefinitions to be loaded');
    }
    return this.props.testDefinitions;
  }

  private openArchiveModal = (itemIndex: number): void => {
    const testDefinition = this.getLoadedTestDefinitions()[itemIndex];
    ArchiveModal.open({
      items: [testDefinition],
      onArchive: this.props.archiveTestDefinitions,
    });
  };

  private createArchiveTestDefinitionHandler = (): ((itemIndex: number) => void) | null =>
    this.props.testDefinitions
      ? (itemIndex: number) => {
          this.openArchiveModal(itemIndex);
        }
      : null;

  private createUnarchiveTestDefinitionHandler = (): ((itemIndex: number) => void) | null =>
    this.props.testDefinitions
      ? (itemIndex: number) => {
          this.props.unarchiveTestDefinitions([this.getLoadedTestDefinitions()[itemIndex].id]);
        }
      : null;

  private handleChangeArchivePurposeFilters = (selectedArchivePurposeFilters: Array<string>): void => {
    this.setState({selectedArchivePurposeFilters});
  };

  private filterForArchivePurpose(
    testDefinitions: Array<TestDefinitionForListing>
  ): Array<TestDefinitionForListing> {
    // TODO(BNCH-102894): ArchiveFilterUtil.filterItemsToPurposes doesn't handle core object archive data format
    const purposes = this.state.selectedArchivePurposeFilters;
    return testDefinitions.filter((item) => {
      if (!isArchived(item) && purposes.includes(NOT_ARCHIVED_PURPOSE_STRING)) {
        return true;
      } else if (isArchived(item)) {
        if (
          item.archiveData != null &&
          item.archiveData.purpose != null &&
          purposes.includes(item.archiveData.purpose)
        ) {
          return true;
        }
      }
      return false;
    });
  }

  private getFilteredTestDefinitions(
    testDefinitions: Optional<Array<TestDefinitionForListing>>
  ): Array<TestDefinitionForListing> {
    let filteredTestDefinitions = this.filterForArchivePurpose(testDefinitions || []);
    if (this.props.customFilterCallback) {
      filteredTestDefinitions = this.props.customFilterCallback(filteredTestDefinitions);
    }

    if (this.state.searchQuery !== EMPTY_SEARCH_QUERY) {
      filteredTestDefinitions = filteredTestDefinitions.filter((testDefinition) =>
        strUtil.caseInsensitiveContains(testDefinition.name, this.state.searchQuery)
      );
    }

    return filteredTestDefinitions;
  }

  private getEmptyMessage(): string {
    if (this.props.errorMessage) {
      return this.props.errorMessage;
    } else {
      return 'No test definitions are present';
    }
  }

  private setSearchQuery = (searchQuery: string): void => {
    this.setState({
      searchQuery,
    });
  };

  render(): JSX.Element {
    const testDefinitionIndicesById = this.props.testDefinitions
      ? // have to cast mapping function output because typescript treats [s.id, i] as ReactText[]
        objectFromEntries(this.props.testDefinitions.map((s, i) => castEntry(s.id, i)))
      : {};
    const filteredTestDefinitions = this.getFilteredTestDefinitions(this.props.testDefinitions);
    const rows: Array<RowProps> | null = filteredTestDefinitions
      ? filteredTestDefinitions.map((testDefinition) => {
          const row: RowProps = {
            id: testDefinition.id,
            itemIndex: testDefinitionIndicesById[testDefinition.id],
            isArchived: isArchived(testDefinition),
            values: this.props.mapTestDefinitionToRowValue(testDefinition),
          };
          if (this.props.mapTestDefinitionToRowPermissions) {
            const permissions = this.props.mapTestDefinitionToRowPermissions(testDefinition);
            row.isArchivable = permissions.isArchivable;
            row.isDeletable = permissions.isDeletable;
          }
          return row;
        })
      : null;
    const isLoading = this.props.testDefinitions == null;
    return (
      <div className="settingsPage" {...getTestProps({component: 'TestDefinitionsListing', element: 'root'})}>
        <div className="settingsPage-contentWrapper">
          <EditableItemTable
            tableName="Test definitions"
            columnHeaders={this.props.columnHeaders}
            emptyMessage={this.getEmptyMessage()}
            isLoading={isLoading}
            rows={rows}
            onCreate={this.props.navigateToCreate}
            getEditUrl={this.getTestDefinitionUrlForRow}
            onArchive={this.createArchiveTestDefinitionHandler()}
            onUnarchive={this.createUnarchiveTestDefinitionHandler()}
            onDelete={noop}
            writable={this.props.isWritable}
            leftSubheaderNode={
              <div className={classNames('u-flex-display', css(styles.searchControls))}>
                <PlainSearchInput
                  className={css(styles.searchInput)}
                  borderRadius={BorderRadius.LARGE}
                  placeholder="Search"
                  value={this.state.searchQuery}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    this.setSearchQuery(e.target.value);
                  }}
                  onEscape={() => {
                    this.setSearchQuery(EMPTY_SEARCH_QUERY);
                  }}
                  onBlur={() => {
                    BenchlingUtils.setQueryParam('q', this.state.searchQuery || null);
                  }}
                />
                <ArchivePurposeDropdown
                  itemIds={
                    isLoading
                      ? []
                      : this.getLoadedTestDefinitions().map((testDefinition) => testDefinition.id)
                  }
                  onChangePurposes={this.handleChangeArchivePurposeFilters}
                  selectedPurposes={this.state.selectedArchivePurposeFilters}
                />
                {this.props.customSearchControls}
              </div>
            }
          />
        </div>
      </div>
    );
  }
}

const styles = StyleSheet.create({
  searchInput: {
    marginRight: 10,
    width: 200,
  },
  searchControls: {
    gap: '10px',
    marginRight: '10px',
  },
});

export default TestDefinitionsListing;
